import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SURV } from "../constants/testIds4";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash, ChartBar, Play, ChartLineUp } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const QT = [
  { v: "nps", label: "NPS (0-10)" },
  { v: "rating_5", label: "Rating (1-5)" },
  { v: "single_choice", label: "Single choice" },
  { v: "multi_choice", label: "Multi choice" },
  { v: "text", label: "Text" },
];

export default function Surveys() {
  const { user, tenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [takeOpen, setTakeOpen] = useState(null);
  const [resultsOpen, setResultsOpen] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", kind: "custom", anonymous: true, start_date: "", end_date: "", questions: [{ text: "", type: "rating_5", choices: [], required: true }] });
  const isHR = ["super_admin", "company_admin", "hr"].includes(user?.role);
  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};

  const load = () => api.get("/surveys", { params }).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  const save = async () => {
    try {
      const payload = { ...form };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/surveys", payload);
      toast.success("Survey created");
      setOpen(false);
      setForm({ title: "", description: "", kind: "custom", anonymous: true, start_date: "", end_date: "", questions: [{ text: "", type: "rating_5", choices: [], required: true }] });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addQ = () => setForm({ ...form, questions: [...form.questions, { text: "", type: "rating_5", choices: [], required: true }] });
  const rmQ = (i) => setForm({ ...form, questions: form.questions.filter((_, x) => x !== i) });
  const setQ = (i, patch) => setForm({ ...form, questions: form.questions.map((q, x) => x === i ? { ...q, ...patch } : q) });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Engagement</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Surveys & eNPS</h1>
          <p className="text-sm text-zinc-500 mt-2">Pulse checks, engagement surveys, and NPS with anonymous responses and aggregated results.</p>
        </div>
        {isHR && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-zenith rounded-sm" data-testid={SURV.createBtn}><Plus size={16} className="mr-1.5" /> New survey</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader><DialogTitle className="font-display text-2xl">Create survey</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1"><Label className="text-xs">Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" data-testid="survey-title-input" /></div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Kind</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="custom">Custom</SelectItem><SelectItem value="enps">eNPS</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Anonymous?</Label>
                  <Select value={String(form.anonymous)} onValueChange={(v) => setForm({ ...form, anonymous: v === "true" })}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Start</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">End</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm" /></div>

                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <Label className="text-xs font-semibold">Questions ({form.questions.length})</Label>
                      <div className="text-[10px] text-zinc-500">Add as many as you need — different types (NPS, rating, choice, text) can be mixed in the same survey.</div>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                    {form.questions.map((q, i) => (
                      <div key={i} className="border border-zinc-200 rounded-sm p-3 space-y-2 bg-zinc-50/50">
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <Label className="text-[10px]">Question {i + 1}</Label>
                            <Input value={q.text} onChange={(e) => setQ(i, { text: e.target.value })} className="rounded-sm h-8 text-xs bg-white" data-testid={`survey-q-${i}`} placeholder={`e.g. How likely are you to recommend us?`} />
                          </div>
                          <div className="w-[150px]">
                            <Label className="text-[10px]">Type</Label>
                            <Select value={q.type} onValueChange={(v) => setQ(i, { type: v })}>
                              <SelectTrigger className="rounded-sm h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                              <SelectContent>{QT.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          {form.questions.length > 1 && <button onClick={() => rmQ(i)} className="text-red-600 mt-5" title="Remove question"><Trash size={14} /></button>}
                        </div>
                        {(q.type === "single_choice" || q.type === "multi_choice") && (
                          <div>
                            <Label className="text-[10px]">Choices (comma-separated)</Label>
                            <Input value={(q.choices || []).join(", ")} onChange={(e) => setQ(i, { choices: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="rounded-sm h-8 text-xs bg-white" placeholder="Yes, No, Maybe" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    onClick={addQ}
                    className="w-full mt-2 rounded-sm btn-zenith text-white font-semibold"
                    data-testid={SURV.addQBtn}
                  >
                    <Plus size={16} className="mr-1.5" /> Add another question
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={save} className="btn-zenith rounded-sm" data-testid={SURV.saveBtn}>Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.length === 0 && <div className="col-span-full border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500 rounded-sm">No surveys yet.</div>}
        {rows.map((s) => (
          <div key={s.id} className="border border-zinc-200 bg-white rounded-sm p-4">
            <div className="flex items-start justify-between">
              <div>
                <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-300 text-[10px] uppercase">{s.kind}</Badge>
                <div className="font-display text-lg font-bold mt-1">{s.title}</div>
              </div>
              <Badge variant="outline" className={`rounded-sm ${s.anonymous ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-zinc-50 border-zinc-200"}`}>{s.anonymous ? "Anon" : "Attributed"}</Badge>
            </div>
            {s.description && <div className="text-xs text-zinc-500 mt-1">{s.description}</div>}
            <div className="text-[10px] font-mono-plex text-zinc-500 mt-2">{s.start_date} → {s.end_date} · {s.questions?.length || 0} q</div>
            <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-100">
              <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => setTakeOpen(s)} data-testid={SURV.takeBtn(s.id)}><Play size={12} className="mr-1" /> Take</Button>
              {isHR && <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => setResultsOpen(s.id)} data-testid={SURV.resultsBtn(s.id)}><ChartBar size={12} className="mr-1" /> Results</Button>}
            </div>
          </div>
        ))}
      </div>

      {takeOpen && <TakeSurveyDialog survey={takeOpen} onClose={() => setTakeOpen(null)} />}
      {resultsOpen && <ResultsDialog sid={resultsOpen} onClose={() => setResultsOpen(null)} />}
    </div>
  );
}

function TakeSurveyDialog({ survey, onClose }) {
  const [answers, setAnswers] = useState({});

  const submit = async () => {
    try {
      await api.post(`/surveys/${survey.id}/respond`, { answers });
      toast.success("Response submitted");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setA = (i, v) => setAnswers({ ...answers, [i]: v });

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">{survey.title}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {(survey.questions || []).map((q, i) => (
            <div key={i} className="border border-zinc-200 rounded-sm p-3">
              <Label className="text-sm">{i + 1}. {q.text} {q.required && <span className="text-red-500">*</span>}</Label>
              <div className="mt-2">
                {q.type === "nps" && (
                  <div className="flex flex-wrap gap-1">
                    {Array.from({ length: 11 }).map((_, n) => (
                      <button key={n} onClick={() => setA(i, n)} data-testid={`nps-${i}-${n}`} className={`w-9 h-9 border rounded-sm text-sm ${answers[i] === n ? "bg-emerald-700 text-white border-emerald-800" : "bg-white border-zinc-200 hover:border-emerald-300"}`}>{n}</button>
                    ))}
                  </div>
                )}
                {q.type === "rating_5" && (
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} onClick={() => setA(i, n)} data-testid={`r5-${i}-${n}`} className={`w-9 h-9 border rounded-sm text-sm ${answers[i] === n ? "bg-emerald-700 text-white border-emerald-800" : "bg-white border-zinc-200 hover:border-emerald-300"}`}>{n}</button>
                    ))}
                  </div>
                )}
                {q.type === "single_choice" && (
                  <div className="space-y-1">
                    {q.choices.map((c) => (
                      <label key={c} className="flex items-center gap-2 text-sm px-2 py-1.5 border border-zinc-200 rounded-sm hover:bg-zinc-50 cursor-pointer">
                        <input type="radio" name={`q${i}`} checked={answers[i] === c} onChange={() => setA(i, c)} /> {c}
                      </label>
                    ))}
                  </div>
                )}
                {q.type === "multi_choice" && (
                  <div className="space-y-1">
                    {q.choices.map((c) => {
                      const sel = (answers[i] || []).includes(c);
                      return (
                        <label key={c} className="flex items-center gap-2 text-sm px-2 py-1.5 border border-zinc-200 rounded-sm hover:bg-zinc-50 cursor-pointer">
                          <input type="checkbox" checked={sel} onChange={() => setA(i, sel ? (answers[i] || []).filter((x) => x !== c) : [...(answers[i] || []), c])} /> {c}
                        </label>
                      );
                    })}
                  </div>
                )}
                {q.type === "text" && (
                  <Textarea value={answers[i] || ""} onChange={(e) => setA(i, e.target.value)} className="rounded-sm" data-testid={`txt-${i}`} />
                )}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} className="btn-zenith rounded-sm" data-testid={SURV.submitBtn}>Submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultsDialog({ sid, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/surveys/${sid}/results`).then(({ data }) => setData(data)).catch(() => {}); }, [sid]);

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">Survey results</DialogTitle></DialogHeader>
        {!data ? <div className="text-sm text-zinc-500 p-6 text-center">Loading…</div> : (
          <div className="space-y-4 max-h-[65vh] overflow-y-auto">
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">{data.response_count} responses</Badge>
              <div className="font-display text-lg font-bold">{data.survey.title}</div>
            </div>
            {(data.aggregated || []).map((q, i) => <ResultBlock key={i} q={q} />)}
          </div>
        )}
        <DialogFooter><Button onClick={onClose} className="rounded-sm">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultBlock({ q }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-4 bg-white">
      <div className="flex items-start justify-between">
        <div className="text-sm font-medium">{q.question}</div>
        <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-200 text-[10px] uppercase">{q.type}</Badge>
      </div>
      <div className="mt-3">
        {q.type === "nps" && (
          <div className="flex items-center gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">NPS</div>
              <div className="font-display text-4xl font-bold text-emerald-700"><ChartLineUp size={20} className="inline mb-1 mr-1" /> {q.nps ?? "—"}</div>
            </div>
            <div className="flex-1 h-[120px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[{ name: "Promoters", v: q.promoters }, { name: "Passives", v: q.passives }, { name: "Detractors", v: q.detractors }]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="name" fontSize={10} />
                  <YAxis fontSize={10} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="v" fill="#047857" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {q.type === "rating_5" && (
          <div className="flex items-center gap-3">
            <div className="font-display text-4xl font-bold text-emerald-700">{q.avg ?? "—"}<span className="text-lg text-zinc-400">/5</span></div>
            <div className="text-xs text-zinc-500">Avg from {q.n} responses</div>
          </div>
        )}
        {(q.type === "single_choice" || q.type === "multi_choice") && (
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={Object.entries(q.counts || {}).map(([name, v]) => ({ name, v }))} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis type="number" fontSize={10} allowDecimals={false} />
                <YAxis dataKey="name" type="category" fontSize={10} width={100} />
                <Tooltip />
                <Bar dataKey="v" fill="#047857" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {q.type === "text" && (
          <div className="space-y-1 max-h-[180px] overflow-y-auto">
            {(q.responses || []).length === 0 && <div className="text-xs text-zinc-500">No responses.</div>}
            {(q.responses || []).map((r, i) => <div key={i} className="text-xs bg-zinc-50 border border-zinc-200 rounded-sm p-2">{r}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}
